import React, { useEffect, useState } from "react";
import isGHMC from "../../Common/helper/helpar";

import TableSkeleton from "./TableSkeleton";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50];
const DEFAULT_PAGE_SIZE = 20;

const thStyle = {
  background: "linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)",
  color: "#555",
  fontWeight: 600,
  fontSize: "12px",
  letterSpacing: "0.5px",
  padding: "10px 14px",
  borderBottom: "2px solid #e0e0e0",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "10px 14px",
  fontSize: "13px",
  color: "#333",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
};

const groupByZone = (data) => {
  const map = {};
  (data || []).forEach((d) => {
    if (!map[d.zoneName]) map[d.zoneName] = [];
    map[d.zoneName].push(d);
  });
  return map;
};


const AllocatedVehicleTable = ({ tableData, isLoading }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const isProd = isGHMC();
  const themeColor = isProd ? "#007bff" : "#EC1117";
  const themeBadgeBg = isProd ? "#eaf2ff" : "#fff0f0";

  const totalPages = Math.ceil(tableData.length / pageSize);
  const paginatedData = tableData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const toggleRow = (index) =>
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));

  const goToPage = (page) => {
    setCurrentPage(page);
    setExpandedRows({});
  };

  useEffect(() => {
    if (!PAGE_SIZE_OPTIONS.includes(pageSize)) {
      setPageSize(DEFAULT_PAGE_SIZE);
      setCurrentPage(1);
      setExpandedRows({});
    }
  }, [pageSize]);

  if (isLoading) return <TableSkeleton />;

  return (
    <div>
      <div
        style={{
          overflowX: "auto",
          maxHeight: "500px",
          overflowY: "auto",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>S.No</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Alloted Vehicle Count</th>
              <th style={thStyle}>Corporation</th>
              <th style={thStyle}>Owner Type</th>
              <th style={thStyle}>Shift</th>
              <th style={{ ...thStyle, textAlign: "center" }}>Details</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((row, pageIndex) => {
              const globalIndex = (currentPage - 1) * pageSize + pageIndex;
              const isExpanded = !!expandedRows[pageIndex];
              const zoneGroups = groupByZone(row.data);

              return (
                <React.Fragment key={globalIndex}>
                  <tr
                    style={{
                      background: isExpanded
                        ? isProd ? "#f0f6ff" : "#fff8f8"
                        : pageIndex % 2 === 0 ? "#fff" : "#fafafa",
                      transition: "background 0.2s",
                    }}
                  >
                    <td style={tdStyle}>{globalIndex + 1}</td>
                     <td style={tdStyle}>{row.date || row.date}</td>
                      <td style={tdStyle}>{row.vehicleCount || row.vehicleCount}</td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 600, color: "#333" }}>
                        {row.corporationName}
                      </span>
                    </td>
                    <td style={tdStyle}>{row.ownerTypeName || row.ownerTypeId}</td>
                    <td style={tdStyle}>{row.shiftName || row.shiftId}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <button
                        onClick={() => toggleRow(pageIndex)}
                        style={{
                          background: isExpanded ? themeColor : "#f5f5f5",
                          color: isExpanded ? "#fff" : "#555",
                          border: "none",
                          borderRadius: "6px",
                          padding: "4px 12px",
                          fontSize: "12px",
                          cursor: "pointer",
                          transition: "all 0.2s",
                          fontWeight: 500,
                        }}
                      >
                        <i
                          className={`fa-solid fa-chevron-${isExpanded ? "up" : "down"} me-1`}
                          style={{ fontSize: "10px" }}
                        />
                        {isExpanded ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: "0",
                          background: "#fefefe",
                          borderBottom: `2px solid ${themeColor}33`,
                        }}
                      >
                        <div style={{ padding: "16px 24px" }}>
                          {Object.entries(zoneGroups).map(([zoneName, circles]) => (
                            <div key={zoneName} style={{ marginBottom: "16px" }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "4px",
                                    height: "16px",
                                    borderRadius: "2px",
                                    background: themeColor,
                                  }}
                                />
                                <span style={{ fontWeight: 600, fontSize: "13px", color: "#444" }}>
                                  {zoneName}
                                </span>
                              </div>

                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: "8px",
                                  paddingLeft: "12px",
                                }}
                              >
                                {circles.map((c, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      background: "#fff",
                                      border: "1px solid #e8e8e8",
                                      borderRadius: "8px",
                                      padding: "6px 12px",
                                      minWidth: "160px",
                                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                    }}
                                  >
                                    <i
                                      className="fa-solid fa-circle-dot"
                                      style={{ color: themeColor, fontSize: "10px" }}
                                    />
                                    <span style={{ fontSize: "13px", color: "#444", flex: 1 }}>
                                      {c.circleName}
                                    </span>
                                    <span
                                      style={{
                                        background: themeBadgeBg,
                                        color: themeColor,
                                        borderRadius: "10px",
                                        padding: "1px 8px",
                                        fontSize: "12px",
                                        fontWeight: 700,
                                      }}
                                    >
                                      {c.vehicleCount}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {!isLoading && tableData.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#999",
                    fontSize: "14px",
                  }}
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && tableData.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "6px",
            padding: "12px 16px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <span style={{ fontSize: "12px", color: "#888" }}>
            Rows per page:
          </span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
              setExpandedRows({});
            }}
            style={{
              border: `1px solid #e0e0e0`,
              borderRadius: "6px",
              padding: "3px 8px",
              fontSize: "12px",
              color: "#555",
              cursor: "pointer",
              marginRight: "8px",
              outline: "none",
            }}
          >
            {PAGE_SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>

          <span style={{ fontSize: "12px", color: "#888", marginRight: "8px" }}>
            Page {currentPage} of {totalPages} &nbsp;|&nbsp; {tableData.length} records
          </span>

          <button
            onClick={() => goToPage(1)}
            disabled={currentPage === 1}
            style={pageBtnStyle(currentPage === 1, false, themeColor)}
          >
            «
          </button>
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={pageBtnStyle(currentPage === 1, false, themeColor)}
          >
            ‹
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
            .reduce((acc, p, i, arr) => {
              if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
              acc.push(p);
              return acc;
            }, [])
            .map((item, i) =>
              item === "..." ? (
                <span key={`ellipsis-${i}`} style={{ fontSize: "13px", color: "#aaa", padding: "0 4px" }}>
                  ...
                </span>
              ) : (
                <button
                  key={item}
                  onClick={() => goToPage(item)}
                  style={pageBtnStyle(false, item === currentPage, themeColor)}
                >
                  {item}
                </button>
              )
            )}

          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={pageBtnStyle(currentPage === totalPages, false, themeColor)}
          >
            ›
          </button>
          <button
            onClick={() => goToPage(totalPages)}
            disabled={currentPage === totalPages}
            style={pageBtnStyle(currentPage === totalPages, false, themeColor)}
          >
            »
          </button>
        </div>
      )}
    </div>
  );
};

const pageBtnStyle = (disabled, active, themeColor) => ({
  minWidth: "30px",
  height: "30px",
  padding: "0 8px",
  border: `1px solid ${active ? themeColor : "#e0e0e0"}`,
  borderRadius: "6px",
  background: active ? themeColor : disabled ? "#f9f9f9" : "#fff",
  color: active ? "#fff" : disabled ? "#ccc" : "#555",
  fontSize: "13px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontWeight: active ? 600 : 400,
  transition: "all 0.15s",
});

export default AllocatedVehicleTable;
